import { perkpointApi, type ApiPage } from "./client";
import { InsightsApi } from "./insights";

export type MemberDisplayStatus = "active" | "at_risk" | "blocked" | "closed";
export type MemberActivity = "high" | "medium" | "low";

export type ApiMemberTier = { id?: string; key: string; name: string; rank: number };

export type ApiMemberRow = {
  member_id: string;
  external_member_id: string;
  program: { id: string; name: string };
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  status: string;
  display_status: MemberDisplayStatus;
  activity: MemberActivity;
  tier: ApiMemberTier | null;
  gold_or_above: boolean;
  segment: { key: string; name: string } | null;
  is_new_this_month: boolean;
  points: {
    available: number;
    pending: number;
    lifetime_earned: number;
    lifetime_redeemed: number;
  };
  enrolled_at: string;
  last_activity_at: string | null;
  days_inactive: number | null;
  next_action: string;
};

export type ApiConsent = { channel: "whatsapp" | "sms" | "email"; status: string; updated_at?: string };

export type ApiMemberDetail = ApiMemberRow & {
  gender: string | null;
  date_of_birth: string | null;
  anniversary: string | null;
  preferred_language: string | null;
  enrolled_outlet: string | null;
  status_reason: string | null;
  risk: "low_risk" | "at_risk" | "blocked";
  engagement: MemberActivity;
  member_since: string;
  primary_segment: { key: string; name: string } | null;
  reward_redemptions: number;
  campaign_participation: number;
  consents: ApiConsent[];
  tier: (ApiMemberTier & { achieved_at?: string; retain_until?: string; state?: string }) | null;
  tier_progress: {
    next_tier_key?: string | null;
    next_tier_name?: string | null;
    remaining?: number;
    current?: number;
    next_entry_value?: number | null;
  } | null;
  progress: { metric: string; value: number; next_tier_key: string | null; next_entry_value: number | null; remaining: number }[];
  points: ApiMemberRow["points"] & {
    outstanding_debt: number;
    lifetime_expired: number;
    expiring_soon: { points: number; date: string | null; within_days: number };
    next_expiry: { points: number; date: string | null } | null;
  };
};

export type MemberListQuery = {
  search?: string;
  status?: string;
  tier?: string;
  activity?: string;
  segment?: string;
  quick_tab?: "all" | "active" | "at_risk" | "new" | "gold_above";
  from?: string;
  to?: string;
  sort?: "enrolled_at" | "points" | "last_activity";
  page?: number;
  pageSize?: number;
};

export type MemberSummary = {
  total: number;
  active: number;
  blocked: number;
  closed: number;
  new_this_month: number;
};

export type EnrolMemberInput = {
  full_name: string;
  phone: string;
  email?: string;
  gender?: string;
  date_of_birth?: string;
  anniversary?: string;
  city?: string;
  preferred_language?: string;
  enrolled_outlet?: string;
  referred_by?: string;
  consents?: { whatsapp?: boolean; sms?: boolean; email?: boolean };
};

export type UpdateMemberInput = Partial<Omit<EnrolMemberInput, "referred_by">>;

export type ChangeTierInput = {
  tier_key: string;
  reason?: string;
  after_change?: "lock" | "standard";
  locked_until?: string;
  issue_welcome_bonus?: boolean;
  notify_whatsapp?: boolean;
};

export type ApiRedemption = {
  txn_code: string;
  created_at: string;
  points: number;
  reward_name: string | null;
  redemption_code: string | null;
  outlet: string | null;
  reversed: boolean;
};

export const memberKeys = {
  all: ["perkpoint", "members"] as const,
  list: (programId: string, query: MemberListQuery) =>
    ["perkpoint", "members", programId, "list", query] as const,
  allPrograms: (query: Record<string, unknown>) => ["perkpoint", "members", "all-programs", query] as const,
  detail: (programId: string, memberId: string) =>
    ["perkpoint", "members", programId, "detail", memberId] as const,
  sub: (programId: string, memberId: string, part: string, extra?: unknown) =>
    ["perkpoint", "members", programId, "detail", memberId, part, extra] as const,
};

const base = (programId: string) => `/admin/programs/${programId}/members`;

export const MembersApi = {
  async list(programId: string, query: MemberListQuery = {}) {
    const { data } = await perkpointApi.get<ApiPage<ApiMemberRow> & { summary: MemberSummary }>(
      base(programId),
      { params: query },
    );
    return data;
  },
  /** Members across programs ("All programs" view / link picker). */
  async listAll(query: MemberListQuery & { program_id?: string } = {}) {
    const { data } = await perkpointApi.get<ApiPage<ApiMemberRow>>("/admin/members", { params: query });
    return data;
  },
  async get(programId: string, memberId: string) {
    const { data } = await perkpointApi.get<ApiMemberDetail>(`${base(programId)}/${memberId}`);
    return data;
  },
  async enrol(programId: string, input: EnrolMemberInput) {
    const { data } = await perkpointApi.post<ApiMemberDetail & { welcome_bonus_points?: number }>(
      base(programId),
      input,
    );
    return data;
  },
  async update(programId: string, memberId: string, input: UpdateMemberInput) {
    const { data } = await perkpointApi.patch<ApiMemberDetail>(`${base(programId)}/${memberId}`, input);
    return data;
  },
  async block(programId: string, memberId: string, reason?: string) {
    const { data } = await perkpointApi.post<ApiMemberDetail>(`${base(programId)}/${memberId}/block`, { reason });
    return data;
  },
  async unblock(programId: string, memberId: string, reason?: string) {
    const { data } = await perkpointApi.post<ApiMemberDetail>(`${base(programId)}/${memberId}/unblock`, { reason });
    return data;
  },
  async changeTier(programId: string, memberId: string, input: ChangeTierInput) {
    const { data } = await perkpointApi.post<{
      direction: "upgrade" | "downgrade" | "assigned";
      tier_key: string;
      welcome_bonus_points: number;
      changed: boolean;
    }>(`${base(programId)}/${memberId}/tier`, input);
    return data;
  },
  async adjustPoints(programId: string, memberId: string, input: { points: number; reason: string }) {
    const { data } = await perkpointApi.post<{ entry_id: string; points: number }>(
      `${base(programId)}/${memberId}/points`,
      input,
    );
    return data;
  },
  async pointsOverview(programId: string, memberId: string, months = 6) {
    const { data } = await perkpointApi.get<{ month: string; earned: number; redeemed: number }[]>(
      `${base(programId)}/${memberId}/points-overview`,
      { params: { months } },
    );
    return data;
  },
  async redemptions(programId: string, memberId: string, limit = 10) {
    const { data } = await perkpointApi.get<ApiRedemption[] | { items: ApiRedemption[] }>(
      `${base(programId)}/${memberId}/redemptions`,
      { params: { limit } },
    );
    return Array.isArray(data) ? data : data.items;
  },
  async participation(programId: string, memberId: string) {
    const { data } = await perkpointApi.get<{
      campaigns: { count: number; last_at: string | null } | null;
      gamification: { count: number; last_at: string | null } | null;
      coupons: { count: number; last_at: string | null } | null;
    }>(`${base(programId)}/${memberId}/participation`);
    return data;
  },
  async exportProfile(programId: string, memberId: string) {
    const { data } = await perkpointApi.get(`${base(programId)}/${memberId}/export`, {
      responseType: "blob",
    });
    return data as Blob;
  },
  async link(programId: string, memberIds: string[]) {
    const { data } = await perkpointApi.post<{
      linked: { source_member_id: string; member_id: string; external_member_id: string }[];
      skipped: { member_id: string; reason: string }[];
    }>(`${base(programId)}/link`, { member_ids: memberIds });
    return data;
  },
  /** Segment filter options: RFM built-ins and custom segments, then condition segments. */
  async segments(programId: string) {
    const overview = await InsightsApi.segments(programId);
    return [
      ...overview.built_in.map((s) => ({ id: s.id, key: s.key, name: s.label, member_count: s.members })),
      ...overview.custom_segments.map((s) => ({ id: s.id, key: s.key, name: s.label, member_count: s.members })),
      ...overview.other_segments.map((s) => ({ id: s.id, key: s.key, name: s.name, member_count: s.member_count })),
    ];
  },
  async unlink(programId: string, memberId: string) {
    await perkpointApi.delete(`${base(programId)}/${memberId}`);
  },
};

/* Display helpers shared by member screens */

export function titleCase(value: string | null | undefined) {
  if (!value) return "";
  return value
    .split(/[_\s-]+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/** Prefer `name`, then first+last, then a readable fallback from email. */
export function memberDisplayName(
  member: {
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null | undefined,
): string {
  const full = member?.name?.trim();
  if (full) return full;
  const parts = [member?.first_name, member?.last_name]
    .map((part) => part?.trim())
    .filter(Boolean) as string[];
  if (parts.length) return parts.join(" ");
  const emailLocal = member?.email?.split("@")[0]?.trim();
  if (emailLocal) {
    return emailLocal
      .replace(/[._+-]+/g, " ")
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
  return "";
}

export function initialsOf(name: string | null | undefined) {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** "Today", "Yesterday", "5 days ago", "3 months ago". */
export function relativeDays(iso: string | null | undefined) {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}
